// Event commission detail (SM-310).
//
// The event-level view of the same question the market page answers,
// one level up: which child markets pay, to whom, at what rate. Mixed
// recipients and mixed rates are the interesting case — an event whose
// children pay different admins is legitimate, and the page has to say
// so plainly rather than picking one to display.
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { LoadFailed } from "@/components/fees/LoadFailed";
import { CommissionMetrics } from "@/components/treasury/CommissionMetrics";
import { CommissionGroupTable } from "@/components/treasury/CommissionGroupTable";
import {
  commissionKeys,
  fetchCommissionSummary,
  fetchTopic,
  keys,
  type CommissionFilters,
} from "@/lib/queries";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";
import { parseReportCurrency } from "@/components/currency/ReportCurrencySelect";
import type { CurrencyCode } from "@/lib/currency";

/**
 * The currency travels with the drill-down (SM-496).
 *
 * A row on the report is read in one currency, and opening it must not
 * change the unit: without the param this page would fall back to
 * settlement's GOLD pin, so clicking a GREEN row would show GOLD figures
 * under the same event name. The report's rows link with it, and the
 * back link carries it home.
 */
export const Route = createFileRoute("/_app/treasury/revenue/events/$eventId")({
  validateSearch: (raw: Record<string, unknown>): { currency: CurrencyCode } => ({
    currency: parseReportCurrency(raw.currency),
  }),
  component: EventCommissionDetail,
});

function EventCommissionDetail() {
  const { eventId } = Route.useParams();
  const { currency } = useSearch({
    from: "/_app/treasury/revenue/events/$eventId",
  });
  const currencyName = useCurrencyName(currency);
  const perms = useMyPerms();
  const redacted = perms.loaded && !perms.has(Perm.CatalogViewAll);

  const filters: CommissionFilters = useMemo(
    () => ({ event_id: eventId, currency }),
    [eventId, currency],
  );
  const summaryQ = useQuery({
    queryKey: commissionKeys.summary(filters),
    queryFn: () => fetchCommissionSummary(filters),
    refetchInterval: 30_000,
  });
  // The name the row was clicked by (SM-344). A redacted viewer arrives
  // here with an opaque digest for an id, which names nothing — they
  // never get a link to this page in the first place.
  const eventQ = useQuery({
    queryKey: keys.topicDetail(eventId),
    queryFn: () => fetchTopic(eventId),
    staleTime: 5 * 60_000,
    retry: 1,
    enabled: !redacted,
  });

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/treasury/revenue/commissions"
          search={{ currency }}
          className="text-xs text-accent hover:underline"
        >
          ← Commissions
        </Link>
        <h1 className="mt-1 font-display text-2xl tracking-tight">
          {eventQ.data?.name ?? "Event commission"}
        </h1>
        <p className="text-xs text-muted">
          {eventQ.data?.name ? "Event commission · " : null}
          <span className="font-mono">{eventId}</span>
        </p>
      </div>

      {summaryQ.isError ? (
        <LoadFailed
          what={`this event's ${currencyName} commission`}
          error={summaryQ.error}
          onRetry={() => void summaryQ.refetch()}
        />
      ) : (
        <CommissionMetrics
          totals={summaryQ.data}
          loading={summaryQ.isLoading}
          currency={currency}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Child markets</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {/* Grouped by market and scoped to this event. Each row's
              totals come from the same aggregate as the strip above, so
              the two always agree — the strip is not a separate sum. */}
          <CommissionGroupTable
            view="market"
            filters={filters}
            redacted={redacted}
            currency={currency}
          />
        </CardBody>
      </Card>

      <p className="max-w-[75ch] text-2xs text-muted">
        Each market locked its own terms when it first published, so an
        event whose children show different recipients or rates is
        expected — a rate changed partway through the event's life applies
        only to markets published after the change.
      </p>
    </div>
  );
}
